import { Button, Modal, ModalBody, ModalContent, ModalHeader, Select, SelectItem, Spinner } from '@nextui-org/react'
import { FC, useEffect, useRef, useState } from 'react'
import { MdStorefront } from 'react-icons/md'
import { useSucursalSso } from './useSucursalSso'

/**
 * Selector de sucursal para la sesión federada de WOM.
 *
 * El ACS manda `sucursales` (nombres) en la aserción SAML y un usuario puede tener
 * varias asignadas. Mientras no elija una, el backoffice no sabe en qué tienda está
 * operando, así que se bloquea el contenido con este modal (no se puede cerrar) hasta
 * que seleccione. Con una sola sucursal no se pregunta nada: se resuelve sola.
 *
 * La elección se traduce a `id_store` con getStoreByName —el SSO no manda ids— y queda
 * en la sesión (`user.sucursal` + `user.id_store` + `user.selectedStore`), que se
 * persiste, así que sobrevive al refresh y se limpia en el logout. Una vez dentro, el
 * cambio de sucursal se hace desde el header (SucursalSwitcher), sin volver a pasar
 * por este modal.
 */
const SucursalGate: FC = () => {
  const { sucursales, pendiente, resolviendo, error, setError, resolver } = useSucursalSso()
  const [draft, setDraft] = useState<string | null>(null)
  // Nombre que ya se intentó resolver: evita que un fallo del endpoint deje al efecto
  // reintentando en bucle.
  const intentada = useRef<string | null>(null)

  useEffect(() => {
    // Sucursal única: se resuelve sin preguntar (un solo intento, en silencio: si falla,
    // el usuario queda como hoy, con id_store 0, en vez de quedar bloqueado).
    if (sucursales.length === 1 && pendiente && intentada.current !== sucursales[0]) {
      intentada.current = sucursales[0]
      resolver(sucursales[0]).catch(() => undefined)
    }
  }, [sucursales, pendiente, resolver])

  if (sucursales.length <= 1 || !pendiente) return null

  return (
    <Modal isOpen hideCloseButton isDismissable={false} isKeyboardDismissDisabled backdrop='blur' placement='center' size='md'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <span className='text-lg font-semibold'>Selecciona tu sucursal</span>
          <span className='text-sm font-normal text-gray-500'>
            Tienes {sucursales.length} sucursales asignadas. Elige en cuál estás operando.
          </span>
        </ModalHeader>
        <ModalBody className='pb-6'>
          <Select
            variant='bordered'
            label='Sucursal'
            placeholder='Selecciona una sucursal'
            isDisabled={resolviendo}
            selectedKeys={draft === null ? [] : [draft]}
            startContent={<MdStorefront className='text-xl flex-shrink-0 text-gray-400' />}
            popoverProps={{ placement: 'bottom' }}
            onChange={(e) => { setDraft(e.target.value === '' ? null : e.target.value); setError('') }}
          >
            {sucursales.map((sucursal) => (
              <SelectItem key={sucursal} textValue={sucursal}>{sucursal}</SelectItem>
            ))}
          </Select>
          {error !== '' ? <p className='text-sm text-danger mt-1'>{error}</p> : null}
          <Button
            color='primary'
            className='w-full mt-2'
            isDisabled={draft === null || resolviendo}
            onPress={() => { if (draft !== null) resolver(draft).catch(() => undefined) }}
          >
            {resolviendo ? <Spinner size='sm' color='white' /> : 'Continuar'}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default SucursalGate
